import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAuth } from "../../context/AuthContext";
import useFetchObjects from "../../api/useFetchObjects";
import { Download } from "@mui/icons-material";
import { exportComprehensiveSaleReportToPDF, exportCustomerReportToPDF } from "../../utils/exportToPDF";
import { exportComprehensiveSaleReportToExcel } from "../../utils/exportSalesReport";

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const ALL_CUSTOMERS_OPTION = { id: "", name: "All Customers" };
const SALE_TYPE_OPTIONS = [
  { value: "all", label: "All Sales" },
  { value: "wholesale", label: "Wholesale" },
  { value: "retail", label: "Retail" },
  // { value: "mixed", label: "Mixed" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "unpaid", label: "Unpaid" },
];

const getSaleTotalAmount = (sale, parseAmountFn) =>
  parseAmountFn(sale?.effective_total_amount ?? sale?.total_amount ?? 0);

const getSalePaymentStatusKey = (sale, returnsList, parseAmountFn) => {
  const paidAmount = parseAmountFn(sale.received_amount || 0);
  const amountAfterDiscount = getSaleTotalAmount(sale, parseAmountFn);
  const returnAmount = (returnsList || [])
    .filter((r) => r.sale === sale.id)
    .reduce((sum, r) => sum + parseAmountFn(r.total_refound_amount), 0);
  const outstanding = Math.max(0, amountAfterDiscount - paidAmount - returnAmount);
  if (outstanding <= 0) return "paid";
  if (paidAmount > 0) return "partial";
  return "unpaid";
};

const countPaymentStatuses = (salesList, returnsList, parseAmountFn) => {
  const counts = { paid: 0, partial: 0, unpaid: 0 };
  salesList.forEach((sale) => {
    const key = getSalePaymentStatusKey(sale, returnsList, parseAmountFn);
    counts[key] += 1;
  });
  return counts;
};

const parseAmountValue = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "object" && value !== null) {
    const currencies = Object.keys(value);
    if (currencies.length > 0) {
      return parseFloat(value[currencies[0]]) || 0;
    }
  }
  const numStr = String(value).replace(/[^0-9.-]/g, "");
  return parseFloat(numStr) || 0;
};

const getSaleType = (sale) => {
  const items = Array.isArray(sale?.sale_items) ? sale.sale_items : [];
  if (!items.length) return "retail";

  const types = new Set(
    items.map((item) => {
      const salePrice = parseAmountValue(item?.sale_price);
      const wholesalePrice = parseAmountValue(item?.product_details?.wholesale_price);
      const retailPrice = parseAmountValue(
        item?.product_details?.retail_price ?? item?.product_details?.sale_price
      );

      if (wholesalePrice > 0 && salePrice === wholesalePrice) return "wholesale";
      if (retailPrice > 0 && salePrice === retailPrice) return "retail";
      if (wholesalePrice > 0 && retailPrice <= 0) return "wholesale";
      return "retail";
    })
  );

  return types.size === 1 ? Array.from(types)[0] : "mixed";
};

const ComprehensiveSaleReport = ({
  selectedBranch = "",
  setSelectedBranch = () => {},
}) => {
  const auth = useAuth();
  const token = auth?.user?.token;

  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedSaleType, setSelectedSaleType] = useState("all");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("all");
  const [viewMode, setViewMode] = useState("simple");

  const queryString = useMemo(() => {
    const params = ["page_size=all"]; // Always fetch all records for reports
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    return `?${params.join("&")}`;
  }, [startDate, endDate, selectedBranch, selectedCustomer]);

  const { data: salesData, isLoading: salesLoading } = useFetchObjects(
    ["sales", queryString],
    `shop/sales/${queryString}`,
    token
  );
  const { data: paymentsData, isLoading: paymentsLoading } = useFetchObjects(
    ["sale-payments", queryString],
    `shop/sale_payments/${queryString}`,
    token
  );
  const { data: returnsData, isLoading: returnsLoading } = useFetchObjects(
    ["sale-returns", queryString],
    `shop/sale-returns/${queryString}`,
    token
  );
  const { data: balancePaymentsData, isLoading: balancePaymentsLoading } =
    useFetchObjects(
      ["customer-balance-payments", queryString],
      `shop/customer-balance-payments/${queryString}`,
      token
    );
  const { data: customersData } = useFetchObjects(
    ["customers"],
    `shop/customers?page_size=all`,
    token
  );
  const { data: branchesData } = useFetchObjects(
    ["report-branches"],
    "shop/shop/?page_size=1000",
    token
  );

  // Fetch customer report data
  const { data: customerReportData } = useFetchObjects(
    ["customer-report", queryString],
    `shop/customer-report/${queryString}`,
    token
  );

  const sales = salesData?.results || salesData || [];
  const payments = paymentsData?.results || paymentsData || [];
  const returns = returnsData?.results || returnsData || [];
  const balancePayments =
    balancePaymentsData?.results || balancePaymentsData || [];
  const customers = customersData?.results || customersData || [];
  const branches = branchesData?.results || branchesData || [];
  const customerOptions = useMemo(
    () => [ALL_CUSTOMERS_OPTION, ...customers],
    [customers]
  );

  const selectedCustomerObj = useMemo(() => {
    if (!selectedCustomer) return null;
    return customers.find((c) => c.id == selectedCustomer) || null;
  }, [customers, selectedCustomer]);
  const selectedCustomerOption = useMemo(() => {
    return (
      customerOptions.find(
        (customer) => String(customer.id) === String(selectedCustomer)
      ) || ALL_CUSTOMERS_OPTION
    );
  }, [customerOptions, selectedCustomer]);

  const balancePaymentsFiltered = useMemo(() => {
    if (!selectedCustomer) return balancePayments;
    return balancePayments.filter(
      (bp) => bp.customer_details?.id == selectedCustomer
    );
  }, [balancePayments, selectedCustomer]);

  const isLoading =
    salesLoading || paymentsLoading || returnsLoading || balancePaymentsLoading;

  const parseAmount = parseAmountValue;
  const getSaleSubtotal = (sale) => parseAmount(sale?.total_amount ?? 0);
  const getSaleDiscount = (sale) => parseAmount(sale?.discount ?? 0);
  // Sale total after discount (use effective_total_amount; fallback to total_amount)
  const getSaleTotal = (sale) =>
    parseAmount(sale?.effective_total_amount ?? sale?.total_amount ?? 0);

  // Filter sales by selected customer only
  const customerFilteredSales = useMemo(() => {
    if (!selectedCustomer) return sales;
    return sales.filter((sale) => sale.customer_details?.id == selectedCustomer);
  }, [sales, selectedCustomer]);

  const returnsForStatus = useMemo(() => {
    if (!selectedCustomer) return returns;
    const customerSaleIds = new Set(
      sales
        .filter((sale) => sale.customer_details?.id == selectedCustomer)
        .map((sale) => sale.id)
    );
    return returns.filter((r) => customerSaleIds.has(r.sale));
  }, [returns, sales, selectedCustomer]);

  const typeFilteredSales = useMemo(() => {
    if (selectedSaleType === "all") return customerFilteredSales;
    return customerFilteredSales.filter(
      (sale) => getSaleType(sale) === selectedSaleType
    );
  }, [customerFilteredSales, selectedSaleType]);

  const filteredSales = useMemo(() => {
    if (selectedPaymentStatus === "all") return typeFilteredSales;
    return typeFilteredSales.filter(
      (sale) =>
        getSalePaymentStatusKey(sale, returnsForStatus, parseAmount) ===
        selectedPaymentStatus
    );
  }, [typeFilteredSales, selectedPaymentStatus, returnsForStatus]);

  const filteredSaleIds = useMemo(
    () => new Set(filteredSales.map((s) => s.id)),
    [filteredSales]
  );

  // When a customer is selected, only show payments for that customer's sales
  const filteredPayments = useMemo(() => {
    if (!selectedCustomer) return payments;
    return payments.filter((p) => filteredSaleIds.has(p.sale));
  }, [payments, selectedCustomer, filteredSaleIds]);

  // When a customer is selected, only show returns for that customer's sales
  const filteredReturns = useMemo(() => {
    if (!selectedCustomer) return returns;
    return returns.filter((r) => filteredSaleIds.has(r.sale));
  }, [returns, selectedCustomer, filteredSaleIds]);

  const saleTypeBreakdown = useMemo(() => {
    const initial = {
      wholesale: { count: 0, amount: 0 },
      retail: { count: 0, amount: 0 },
      mixed: { count: 0, amount: 0 },
    };

    customerFilteredSales.forEach((sale) => {
      const type = getSaleType(sale);
      initial[type].count += 1;
      initial[type].amount += getSaleTotal(sale);
    });

    return initial;
  }, [customerFilteredSales]);

  const totalBalancePaymentsAmount = balancePaymentsFiltered.reduce(
    (sum, p) => sum + parseAmount(p.amount),
    0
  );
  const totalSalePaymentsAmount = filteredPayments.reduce(
    (sum, p) => sum + parseAmount(p.amount),
    0
  );
  const totalRevenue = totalSalePaymentsAmount + totalBalancePaymentsAmount;

  const previousBalanceAmount = selectedCustomerObj
    ? parseAmount(selectedCustomerObj.opening_balance)
    : 0;
  const previousBalanceCurrency =
    selectedCustomerObj?.opening_balance_currency_details?.title || "";

  // When "All Customers": total opening balance across all customers (for report and PDF)
  const totalOpeningBalanceAmount = useMemo(() => {
    if (selectedCustomer) return 0;
    return (customers || []).reduce(
      (sum, c) => sum + parseAmount(c.opening_balance),
      0
    );
  }, [customers, selectedCustomer]);
  const totalOpeningBalanceByCurrency = useMemo(() => {
    if (selectedCustomer) return {};
    const byCur = {};
    (customers || []).forEach((c) => {
      const cur = c.opening_balance_currency_details?.title || "AF";
      byCur[cur] = (byCur[cur] || 0) + parseAmount(c.opening_balance);
    });
    return byCur;
  }, [customers, selectedCustomer]);

  const summary = {
    previous_balance: selectedCustomer
      ? previousBalanceAmount
      : totalOpeningBalanceAmount,
    previous_balance_currency: selectedCustomer
      ? previousBalanceCurrency
      : Object.keys(totalOpeningBalanceByCurrency).length === 1
        ? Object.keys(totalOpeningBalanceByCurrency)[0]
        : Object.keys(totalOpeningBalanceByCurrency).length > 1
          ? "Various"
          : "",
    total_opening_balance_by_currency: totalOpeningBalanceByCurrency,
    total_sales: {
      count: filteredSales.length,
      amount: filteredSales.reduce(
        (sum, s) => sum + getSaleSubtotal(s),
        0
      ),
      discount_amount: filteredSales.reduce(
        (sum, s) => sum + getSaleDiscount(s),
        0
      ),
      after_discount_amount: filteredSales.reduce(
        (sum, s) => sum + getSaleTotal(s),
        0
      ),
    },
    total_payments: {
      count: filteredPayments.length,
      amount: totalSalePaymentsAmount,
    },
    total_balance_payments: {
      count: balancePaymentsFiltered.length,
      amount: totalBalancePaymentsAmount,
    },
    total_revenue: totalRevenue,
    total_returns: {
      count: filteredReturns.length,
      amount: filteredReturns.reduce(
        (sum, r) => sum + parseAmount(r.total_refound_amount),
        0
      ),
    },
    total_paid: filteredSales.reduce(
      (sum, s) => sum + parseAmount(s.received_amount || 0),
      0
    ),
    total_remaining: filteredSales.reduce(
      (sum, s) => sum + parseAmount(s.total_remained_amount || 0),
      0
    ),
    outstanding_amount: filteredSales.reduce((sum, s) => {
      const saleAmount = getSaleTotal(s);
      const paidAmount = parseAmount(s.received_amount || 0);
      const saleReturns = filteredReturns.filter((r) => r.sale === s.id);
      const returnAmount = saleReturns.reduce(
        (rSum, r) => rSum + parseAmount(r.total_refound_amount),
        0
      );
      return sum + Math.max(0, saleAmount - paidAmount - returnAmount);
    }, 0),
    // When customer selected: total outstanding = previous + sales outstanding - balance payments (matches customer page)
    // When all customers: outstanding from sales + total opening balance (opening balance included in report)
    outstanding_including_previous: selectedCustomer
      ? Math.max(
          0,
          previousBalanceAmount +
            filteredSales.reduce((sum, s) => {
              const saleAmount = getSaleTotal(s);
              const paidAmount = parseAmount(s.received_amount || 0);
              const saleReturns = filteredReturns.filter((r) => r.sale === s.id);
              const returnAmount = saleReturns.reduce(
                (rSum, r) => rSum + parseAmount(r.total_refound_amount),
                0
              );
              return sum + Math.max(0, saleAmount - paidAmount - returnAmount);
            }, 0) -
            totalBalancePaymentsAmount
        )
      : Math.max(
          0,
          filteredSales.reduce((sum, s) => {
            const saleAmount = getSaleTotal(s);
            const paidAmount = parseAmount(s.received_amount || 0);
            const saleReturns = filteredReturns.filter((r) => r.sale === s.id);
            const returnAmount = saleReturns.reduce(
              (rSum, r) => rSum + parseAmount(r.total_refound_amount),
              0
            );
            return sum + Math.max(0, saleAmount - paidAmount - returnAmount);
          }, 0) + totalOpeningBalanceAmount
        ),
    net_sales:
      filteredSales.reduce((sum, s) => sum + getSaleTotal(s), 0) -
      filteredReturns.reduce(
        (sum, r) => sum + parseAmount(r.total_refound_amount),
        0
      ),
    payment_status: countPaymentStatuses(
      typeFilteredSales,
      returnsForStatus,
      parseAmount
    ),
    is_all_customers: !selectedCustomer,
    sale_type_filter: selectedSaleType,
    payment_status_filter: selectedPaymentStatus,
    sale_type_breakdown: saleTypeBreakdown,
  };

  const displayOutstanding =
    summary.outstanding_including_previous != null
      ? summary.outstanding_including_previous
      : summary.outstanding_amount;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue?.id ? String(newValue.id) : "");
  };

  const exportToPDF = () => {
    const exportData = {
      sales: filteredSales,
      payments: filteredPayments,
      returns: filteredReturns,
      balancePayments: balancePaymentsFiltered,
      summary,
      customers,
      selectedSaleType,
      selectedPaymentStatus,
    };
    exportComprehensiveSaleReportToPDF(exportData);
  };

  const exportToExcel = () => {
    const exportData = {
      sales: filteredSales,
      payments: filteredPayments,
      returns: filteredReturns,
      balancePayments: balancePaymentsFiltered,
      summary,
      selectedCustomerName: selectedCustomerObj?.name || "",
      selectedSaleType,
      selectedPaymentStatus,
      startDate: startDate?.format("YYYY-MM-DD") || "",
      endDate: endDate?.format("YYYY-MM-DD") || "",
    };
    exportComprehensiveSaleReportToExcel(exportData);
  };

  const exportCustomerReport = () => {
    if (customerReportData) {
      exportCustomerReportToPDF(customerReportData);
    }
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedCustomer("");
    setSelectedSaleType("all");
    setSelectedPaymentStatus("all");
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading sales data...</Typography>
      </Box>
    );
  }

  const tableHeadCellSx = {
    backgroundColor: "#12344d",
    color: "#ffffff",
    fontWeight: 700,
  };
  const totalRowSx = {
    backgroundColor: "#e8f1fb",
    "& .MuiTableCell-root": {
      color: "#102a43",
      fontWeight: 700,
    },
  };

  return (
    <Box sx={{ width: "100%", color: "#102a43" }}>
      <Typography variant="h4" sx={{ mb: 3, color: "#102a43", fontWeight: 700 }}>
        Comprehensive Sales Report
      </Typography>

      {/* View Mode Toggle */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant={viewMode === "simple" ? "contained" : "outlined"}
          onClick={() => setViewMode("simple")}
          sx={{ mr: 1 }}
        >
          📊 Simple View
        </Button>
        <Button
          variant={viewMode === "detailed" ? "contained" : "outlined"}
          onClick={() => setViewMode("detailed")}
        >
          📋 Detailed View
        </Button>
      </Box>

      {viewMode === "simple" ? (
        /* Simple View */
        <Box>
          {/* Filters for Simple View */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: "#f8fbff",
              border: "1px solid #d9e2ec",
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={handleCustomerChange}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField {...params} label="Customer" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="simple-sale-report-branch-label">
                      Branch
                    </InputLabel>
                    <Select
                      labelId="simple-sale-report-branch-label"
                      value={selectedBranch}
                      label="Branch"
                      onChange={(e) => setSelectedBranch(e.target.value)}
                    >
                      <MenuItem value="">All Branches</MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={String(branch.id)}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Sale Type"
                    value={selectedSaleType}
                    onChange={(e) => setSelectedSaleType(e.target.value)}
                  >
                    {SALE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Payment Status"
                    value={selectedPaymentStatus}
                    onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                  >
                    {PAYMENT_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={exportToPDF}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export Excel
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportCustomerReport}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Customer Report
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Paper>

          <Paper
            sx={{
              p: 3,
              mb: 3,
              textAlign: "center",
              backgroundColor: "#f8fbff",
              border: "1px solid #d9e2ec",
            }}
          >
            <Typography
              variant="h5"
              sx={{ mb: 2, color: "#102a43", fontWeight: 700 }}
            >
              📈 Quick Overview
            </Typography>
            <Grid container spacing={2}>
              {(selectedCustomer || totalOpeningBalanceAmount > 0) && (
                <Grid item xs={12} md={2.4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#243b53",
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      {summary.previous_balance.toFixed(2)}{" "}
                      {summary.previous_balance_currency || ""}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      Previous Balance (Opening)
                    </Typography>
                    <Typography variant="caption" sx={{ color: "white" }}>
                      {selectedCustomer
                        ? selectedCustomerObj?.name || "Customer"
                        : "All customers"}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#0f4c81",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_sales.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Total Sales
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.total_sales.amount.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    Discount: {summary.total_sales.discount_amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#1f7a4c",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_revenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Revenue (Collected)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    Sale payments + Balance payments
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#8a6d1f",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {displayOutstanding.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Outstanding
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    {selectedCustomer
                      ? "Previous + Sales (after returns)"
                      : "Opening balance + Sales (after returns)"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#7d3c98",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.sale_type_breakdown.wholesale.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Wholesale Sales
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.sale_type_breakdown.wholesale.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#8d6e63",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.sale_type_breakdown.retail.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Retail Sales
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.sale_type_breakdown.retail.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#176087",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_returns.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Returns
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.total_returns.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#5b2a86",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_balance_payments.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Balance Payments
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.total_balance_payments.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#334e68",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "white", mb: 1 }}>
                    Payment Status
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ✅ Paid: {summary.payment_status.paid}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ⏳ Partial: {summary.payment_status.partial}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ❌ Unpaid: {summary.payment_status.unpaid}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            sx={{
              p: 2,
              backgroundColor: "#ffffff",
              border: "1px solid #d9e2ec",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent Sales
            </Typography>
            {filteredSales.slice(0, 5).map((sale) => {
              const saleReturns = filteredReturns.filter(
                (r) => r.sale === sale.id
              );
              const returnAmount = saleReturns.reduce(
                (sum, r) => sum + parseAmount(r.total_refound_amount),
                0
              );
              const remaining =
                getSaleTotal(sale) -
                parseAmount(sale.received_amount || 0) -
                returnAmount;

              return (
                <Box
                  key={sale.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    mb: 1,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      #{sale.id} -{" "}
                      {sale.customer_details?.name || "Walk-in Customer"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sale.sale_date}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="h6" color="primary">
                      {getSaleSubtotal(sale).toFixed(2)}
                    </Typography>
                    <Chip
                      label={remaining <= 0 ? "✅ Paid" : "⏳ Pending"}
                      size="small"
                      color={remaining <= 0 ? "success" : "warning"}
                    />
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Box>
      ) : (
        /* Detailed View */
        <Box>
          {/* Comprehensive Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {selectedCustomer && (
              <Grid item xs={12} md={2}>
                <Card sx={{ bgcolor: "grey.700" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ color: "white" }}>
                      Previous Balance (Opening)
                    </Typography>
                    <Typography variant="h4" sx={{ color: "white" }}>
                      {summary.previous_balance.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {summary.previous_balance_currency || "—"} •{" "}
                      {selectedCustomerObj?.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "primary.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="primary">
                    Total Sales
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {summary.total_sales.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.total_sales.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "success.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="success.main">
                    Revenue
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {summary.total_revenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    Sale + Balance payments
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "warning.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="warning.main">
                    Outstanding
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {displayOutstanding.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    {selectedCustomer ? "Previous + Sales" : "After Returns"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "#f3e5f5" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" sx={{ color: "#6a1b9a" }}>
                    Wholesale
                  </Typography>
                  <Typography variant="h4" sx={{ color: "#6a1b9a" }}>
                    {summary.sale_type_breakdown.wholesale.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.sale_type_breakdown.wholesale.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "#efebe9" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" sx={{ color: "#6d4c41" }}>
                    Retail
                  </Typography>
                  <Typography variant="h4" sx={{ color: "#6d4c41" }}>
                    {summary.sale_type_breakdown.retail.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.sale_type_breakdown.retail.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "info.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="info.main">
                    Total Returns
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {summary.total_returns.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.total_returns.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "secondary.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="secondary.main">
                    Balance Payments
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {summary.total_balance_payments.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.total_balance_payments.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "grey.200" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="text.secondary">
                    Net Sales
                  </Typography>
                  <Typography variant="h4" color="text.secondary">
                    {summary.net_sales.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">After Returns</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Financial Summary */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              bgcolor: "#ffffff",
              border: "1px solid #d9e2ec",
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, textAlign: "center", color: "#102a43", fontWeight: 700 }}
            >
              💰 Complete Financial Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "primary.main",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                    Sales Summary
                  </Typography>
                  {selectedCustomer && (
                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                      Previous Balance (Opening):{" "}
                      <strong>
                        {summary.previous_balance.toFixed(2)}{" "}
                        {summary.previous_balance_currency || ""}
                      </strong>
                    </Typography>
                  )}
                  <Typography variant="body1">
                    Invoice Subtotal:{" "}
                    <strong>{summary.total_sales.amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Discount Amount:{" "}
                    <strong>{summary.total_sales.discount_amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Paid Amount:{" "}
                    <strong>{summary.total_paid.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Outstanding Amount:{" "}
                    <strong>{displayOutstanding.toFixed(2)}</strong>
                  </Typography>
                  {selectedCustomer && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      (Previous {summary.previous_balance.toFixed(2)} + Sales outstanding {summary.outstanding_amount.toFixed(2)})
                    </Typography>
                  )}
                  {selectedCustomer &&
                    selectedCustomerObj?.total_owed_by_currency &&
                    typeof selectedCustomerObj.total_owed_by_currency ===
                      "object" && (
                      <Typography
                        variant="body1"
                        sx={{ mt: 1, fontWeight: "bold" }}
                      >
                        Total Owed (customer):{" "}
                        {Object.entries(
                          selectedCustomerObj.total_owed_by_currency
                        )
                          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
                          .join(", ") || "—"}
                      </Typography>
                    )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Collection Progress:{" "}
                    {summary.total_sales.after_discount_amount > 0
                      ? (
                          (summary.total_paid /
                            summary.total_sales.after_discount_amount) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "success.main",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                    Revenue (Collected)
                  </Typography>
                  <Typography variant="body1">
                    Sale Payments:{" "}
                    <strong>{summary.total_payments.amount.toFixed(2)}</strong>{" "}
                    ({summary.total_payments.count})
                  </Typography>
                  <Typography variant="body1">
                    Balance Payments:{" "}
                    <strong>
                      {summary.total_balance_payments.amount.toFixed(2)}
                    </strong>{" "}
                    ({summary.total_balance_payments.count})
                  </Typography>
                  <Typography variant="body1">
                    Total Revenue:{" "}
                    <strong>{summary.total_revenue.toFixed(2)}</strong>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "info.main",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" color="info.main" sx={{ mb: 1 }}>
                    Returns & Net
                  </Typography>
                  <Typography variant="body1">
                    Total Returns:{" "}
                    <strong>{summary.total_returns.amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Net Sales Value:{" "}
                    <strong>{summary.net_sales.toFixed(2)}</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Note: Returns are deducted from outstanding amount for
                    accurate balance calculation
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={handleCustomerChange}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField {...params} label="Customer" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="detailed-sale-report-branch-label">
                      Branch
                    </InputLabel>
                    <Select
                      labelId="detailed-sale-report-branch-label"
                      value={selectedBranch}
                      label="Branch"
                      onChange={(e) => setSelectedBranch(e.target.value)}
                    >
                      <MenuItem value="">All Branches</MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={String(branch.id)}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Sale Type"
                    value={selectedSaleType}
                    onChange={(e) => setSelectedSaleType(e.target.value)}
                  >
                    {SALE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Payment Status"
                    value={selectedPaymentStatus}
                    onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                  >
                    {PAYMENT_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={exportToPDF}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export Excel
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>

          {/* Tabs */}
          <Paper sx={{ width: "100%" }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={`Sales (${filteredSales.length})`} />
              <Tab label={`Sale Payments (${filteredPayments.length})`} />
              <Tab label={`Returns (${filteredReturns.length})`} />
              <Tab
                label={`Balance Payments (${balancePaymentsFiltered.length})`}
              />
            </Tabs>

            {/* Sales Tab */}
            <TabPanel value={tabValue} index={0}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Customer</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Type</TableCell>
                      <TableCell sx={tableHeadCellSx}>Total Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Discount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Paid</TableCell>
                      <TableCell sx={tableHeadCellSx}>Returns</TableCell>
                      <TableCell sx={tableHeadCellSx}>Outstanding</TableCell>
                      <TableCell sx={tableHeadCellSx}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSales.map((sale) => {
                      const saleReturns = filteredReturns.filter(
                        (r) => r.sale === sale.id
                      );
                      const returnAmount = saleReturns.reduce(
                        (sum, r) => sum + parseAmount(r.total_refound_amount),
                        0
                      );
                      const paidAmount = parseAmount(sale.received_amount || 0);
                      const totalAmount = getSaleSubtotal(sale);
                      const discountAmount = getSaleDiscount(sale);
                      const amountAfterDiscount = getSaleTotal(sale);
                      const outstanding = Math.max(
                        0,
                        amountAfterDiscount - paidAmount - returnAmount
                      );
                      const status =
                        outstanding <= 0
                          ? "Paid"
                          : paidAmount > 0
                          ? "Partial"
                          : "Unpaid";
                      const statusColor =
                        outstanding <= 0
                          ? "success"
                          : paidAmount > 0
                          ? "warning"
                          : "error";

                      return (
                        <TableRow key={sale.id}>
                          <TableCell>#{sale.id}</TableCell>
                          <TableCell>
                            {sale.customer_details?.name || "Walk-in Customer"}
                          </TableCell>
                          <TableCell>{sale.sale_date}</TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {getSaleType(sale)}
                          </TableCell>
                          <TableCell>{totalAmount.toFixed(2)}</TableCell>
                          <TableCell>{discountAmount.toFixed(2)}</TableCell>
                          <TableCell>{paidAmount.toFixed(2)}</TableCell>
                          <TableCell>{returnAmount.toFixed(2)}</TableCell>
                          <TableCell>{outstanding.toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={status}
                              size="small"
                              color={statusColor}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={4}>
                        <strong>TOTAL ({filteredSales.length} sales)</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_sales.amount.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_sales.discount_amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_paid.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_returns.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>{displayOutstanding.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Outstanding</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Payments Tab */}
            <TabPanel value={tabValue} index={1}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Sale</TableCell>
                      <TableCell sx={tableHeadCellSx}>Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Method</TableCell>
                      <TableCell sx={tableHeadCellSx}>Currency</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>#{payment.id}</TableCell>
                        <TableCell>Sale #{payment.sale}</TableCell>
                        <TableCell>
                          {parseAmount(payment.amount).toFixed(2)}
                        </TableCell>
                        <TableCell>{payment.payment_date}</TableCell>
                        <TableCell>
                          {payment.payment_method || "Cash"}
                        </TableCell>
                        <TableCell>
                          {payment.currency_details?.title || "USD"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={2}>
                        <strong>
                          TOTAL ({filteredPayments.length} payments)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_payments.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell colSpan={3}>
                        <strong>Total Collected</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Returns Tab */}
            <TabPanel value={tabValue} index={2}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Sale</TableCell>
                      <TableCell sx={tableHeadCellSx}>Return Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Reason</TableCell>
                      <TableCell sx={tableHeadCellSx}>Refund Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReturns.map((returnItem) => (
                      <TableRow key={returnItem.id}>
                        <TableCell>#{returnItem.id}</TableCell>
                        <TableCell>Sale #{returnItem.sale}</TableCell>
                        <TableCell>{returnItem.return_date}</TableCell>
                        <TableCell>
                          {returnItem.reason || "No reason provided"}
                        </TableCell>
                        <TableCell>
                          {parseAmount(returnItem.total_refound_amount).toFixed(
                            2
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={returnItem.return_items?.length || 0}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={4}>
                        <strong>
                          TOTAL ({filteredReturns.length} returns)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_returns.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {filteredReturns.reduce(
                            (sum, r) => sum + (r.return_items?.length || 0),
                            0
                          )}{" "}
                          items returned
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Balance Payments Tab */}
            <TabPanel value={tabValue} index={3}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Customer</TableCell>
                      <TableCell sx={tableHeadCellSx}>Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Currency</TableCell>
                      <TableCell sx={tableHeadCellSx}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {balancePaymentsFiltered.map((bp) => (
                      <TableRow key={bp.id}>
                        <TableCell>#{bp.id}</TableCell>
                        <TableCell>
                          {bp.customer_details?.name || "-"}
                        </TableCell>
                        <TableCell>
                          {parseAmount(bp.amount).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {bp.payment_date
                            ? new Date(bp.payment_date).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {bp.currency_details?.title || "-"}
                        </TableCell>
                        <TableCell>{bp.notes || "-"}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={2}>
                        <strong>
                          TOTAL ({balancePaymentsFiltered.length} balance
                          payments)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_balance_payments.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell colSpan={3}>
                        <strong>Total collected (balance)</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ComprehensiveSaleReport;
